/**
 * The screen of a feature above the plan (`tier_not_eligible`, #1897): says
 * which plan the feature is sold from, shows what it gives, and points to the
 * plans — for a role that can open billing — or to the account owner.
 *
 * One component for every page-level refusal (the generic `FeatureGate`, the
 * deal queue and card, the Voice of Customer section) and for the Bitrix24
 * assistant dialog: it takes only the flag and derives the rest —
 *
 * - the plan from the catalog gate (`findTierFeatureGate(flag).minTier`),
 *   never written next to the flag key;
 * - the value preview from the flag's copy, when it has any; a flag without
 *   a preview gets the screen without the block;
 * - the CTA from the role: billing is the account owner's screen, anyone else
 *   would land on settings without it. Outside the permission context (the
 *   Bitrix24 iframe, where there is no payment, and tests) there is no link.
 *   In the web app the screen says to ask the owner; in the iframe every role
 *   reads the same (the permission context is empty, so a `client` is
 *   indistinguishable from anyone else): the owner changes the plan in the
 *   Aelo web app, Settings → Plan & Billing.
 * - the Pro trial's line (#2816): before it starts and after it ends
 *   (`ProTrialLockNote`).
 */
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Check, Lock } from 'lucide-react';
import type { FeatureFlags } from '@/contexts/FeatureFlagsContext';
import { ProTrialLockNote } from '@/components/ui/ProTrialLockNote';
import { useTierPlanCta } from '@/hooks/useTierPlanCta';
import { PLANS_HREF } from '@/lib/plans-href';
import { cn } from '@/lib/utils';

/**
 * The features that have preview copy (`featureGate.preview.<flag>`, four
 * locales — a test holds the list and the bundles together). Every bullet
 * there states only what the code does today.
 */
export const TIER_PREVIEW_FLAGS = [
  'enableAIAssistant',
  'enableDashboardSales',
  'enableDealStateSummary',
  'enableVoiceOfCustomer',
] as const satisfies readonly (keyof FeatureFlags)[];

const PREVIEW_BULLETS = ['b1', 'b2', 'b3'] as const;

const BUTTON_CLASS =
  'mt-2 rounded-button bg-primary px-4 py-2 text-sm font-medium text-bg transition-all hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface TierLockedScreenProps {
  flag: keyof FeatureFlags;
  /**
   * «Go to Overview» under the text, for a screen that IS the page (a role
   * without billing has no other way out of it). Off inside a section of a
   * page and in a dialog, where the way out is already there.
   */
  showBackLink?: boolean;
  /** Inside a dialog: the page's tall vertical padding would push it past a short viewport. */
  compact?: boolean;
}

export function TierLockedScreen({
  flag,
  showBackLink = false,
  compact = false,
}: TierLockedScreenProps) {
  const { t } = useTranslation('common');
  const { plan, canOpenPlans, embedded } = useTierPlanCta(flag);
  const hasPreview = (TIER_PREVIEW_FLAGS as readonly string[]).includes(flag);

  return (
    <div
      data-testid="tier-locked"
      className={cn(
        'flex flex-col items-center justify-center gap-4 text-center',
        compact ? 'py-2' : 'py-24'
      )}
    >
      <div className="rounded-full bg-bg-elevated p-4">
        <Lock className="h-10 w-10 text-fg-muted" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-base font-medium text-fg">
          {plan
            ? t('featureGate.tier.title', { plan })
            : t('featureGate.tier.titleGeneric')}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          {t(
            canOpenPlans
              ? 'featureGate.tier.description'
              : embedded
                ? 'featureGate.tier.askOwnerEmbedded'
                : 'featureGate.tier.askOwner'
          )}
        </p>
        <ProTrialLockNote flag={flag} className="mt-2 text-fg" />
      </div>
      {hasPreview && (
        <section
          data-testid="tier-locked-preview"
          className="w-full max-w-prose rounded-card border border-border bg-bg-surface p-4 text-left"
        >
          <h3 className="text-sm font-medium text-fg">
            {t(`featureGate.preview.${flag}.headline`)}
          </h3>
          <ul className="mt-2 space-y-2">
            {PREVIEW_BULLETS.map((bullet) => (
              <li key={bullet} className="flex gap-2 text-sm text-fg-muted">
                <Check
                  className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <span>{t(`featureGate.preview.${flag}.${bullet}`)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {canOpenPlans ? (
        <a href={PLANS_HREF} className={BUTTON_CLASS}>
          {t('featureGate.tier.cta')}
        </a>
      ) : (
        showBackLink && (
          <Link to="/dashboard" className={BUTTON_CLASS}>
            {t('featureGate.cta', 'Go to Overview')}
          </Link>
        )
      )}
    </div>
  );
}
